import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import supportApi from "../../../../configs/apis/support";
import { getTicket } from "../action/support.action";

/**
 * Polled unread-message state for the Support nav badge on the mgmt portal.
 * Populated by useSupportUnreadPoll (30s interval).
 *
 * unreadByTicket keeps the per-ticket counts so opening a ticket detail can
 * decrement totalUnread optimistically without waiting for the next poll.
 */
const SUPPORT_NOTIFICATIONS = "supportNotifications";

export const fetchSupportUnreadSummary = createAsyncThunk(
  `${SUPPORT_NOTIFICATIONS}/fetch`,
  async (_, { rejectWithValue }) =>
    supportApi
      .getUnreadSummary()
      .then((response) => response.data)
      .catch((error) => rejectWithValue(error))
);

const initialState = {
  totalUnread: 0,
  unreadByTicket: {},
  loaded: false,
  lastPolledAt: null,
};

const slice = createSlice({
  name: SUPPORT_NOTIFICATIONS,
  initialState,
  reducers: {
    clearTicketUnread(state, action) {
      const ticketId = action.payload;
      if (!ticketId) return;
      const current = state.unreadByTicket[ticketId] || 0;
      if (current > 0) {
        state.totalUnread = Math.max(0, state.totalUnread - current);
        delete state.unreadByTicket[ticketId];
      }
    },
    resetSupportUnread() {
      return initialState;
    },
  },
  extraReducers: {
    [fetchSupportUnreadSummary.fulfilled]: (state, action) => {
      const payload = action.payload || {};
      state.totalUnread = payload.total_unread || 0;
      // Object.fromEntries keeps the reduction immutable so we don't
      // hit no-param-reassign on the accumulator.
      state.unreadByTicket = Object.fromEntries(
        (payload.tickets || [])
          .filter((t) => t && t.ticket_id)
          .map((t) => [t.ticket_id, t.unread_count || 0])
      );
      state.loaded = true;
      state.lastPolledAt = Date.now();
    },
    [fetchSupportUnreadSummary.rejected]: (state) => {
      state.loaded = true;
    },
    // Opening a ticket detail on the mgmt portal also auto-marks it read on
    // the server; mirror that optimistically so the badge decrements now.
    [getTicket.fulfilled]: (state, action) => {
      const ticketId =
        action.payload?.data?.id ||
        action.payload?.data?.ticket_id ||
        action.payload?.id;
      if (!ticketId) return;
      const current = state.unreadByTicket[ticketId] || 0;
      if (current > 0) {
        state.totalUnread = Math.max(0, state.totalUnread - current);
        delete state.unreadByTicket[ticketId];
      }
    },
  },
});

export const { clearTicketUnread, resetSupportUnread } = slice.actions;
export const supportNotificationsReducer = slice.reducer;

export const selectSupportTotalUnread = (state) =>
  state.supportNotifications?.totalUnread || 0;
export const selectSupportUnreadByTicket = (state) =>
  state.supportNotifications?.unreadByTicket || {};
